import React, { useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import {
  Loader2, Lock, Upload, Trash2, Check, X, ScanText, RefreshCw, LogOut,
  Edit3, Save, ChevronUp, ChevronDown, CalendarDays, Users, MessageSquare,
  Sparkles, Send, Facebook, Instagram, Image as ImageIcon, Star, Phone,
  ImagePlus, FileText, PartyPopper, Plus, FileImage
} from "lucide-react";
import { api, adminAxios, CATEGORY_LABELS, CATEGORY_ORDER } from "@/lib/api";

export default function Admin() {
  const [code, setCode] = useState(localStorage.getItem("globo_admin_code") || "");
  const [authed, setAuthed] = useState(!!localStorage.getItem("globo_admin_code"));
  const [tab, setTab] = useState("dashboard");

  const login = async (e) => {
    e.preventDefault();
    try {
      const res = await api.post("/admin/login", { code });
      if (res.data?.success) {
        localStorage.setItem("globo_admin_code", code);
        setAuthed(true);
        toast.success("Bienvenue !");
      }
    } catch { toast.error("Code admin invalide"); }
  };
  const logout = () => { localStorage.removeItem("globo_admin_code"); setCode(""); setAuthed(false); };

  if (!authed) {
    return (
      <div data-testid="admin-login-page" className="pt-32 pb-24 min-h-[80vh] flex items-center">
        <div className="max-w-md mx-auto w-full px-6">
          <div className="card-warm p-8 text-center">
            <div className="w-16 h-16 mx-auto rounded-full bg-[#C04E38]/10 grid place-items-center text-[#C04E38] mb-4"><Lock size={28} /></div>
            <h1 className="text-3xl mb-2">Espace admin</h1>
            <p className="text-sm text-[#5C5A56] mb-6">Saisissez le code pour accéder au tableau de bord.</p>
            <form onSubmit={login} className="space-y-3">
              <input type="password" placeholder="Code admin" value={code} onChange={(e)=>setCode(e.target.value)}
                className="input-warm text-center" data-testid="admin-code-input" required/>
              <button type="submit" className="btn-primary w-full" data-testid="admin-login-btn">Entrer</button>
            </form>
          </div>
        </div>
      </div>
    );
  }

  const tabs = [
    { k: "dashboard", label: "Tableau de bord", icon: Sparkles },
    { k: "reservations", label: "Réservations", icon: CalendarDays },
    { k: "privatizations", label: "Privatisations", icon: Users },
    { k: "events", label: "Soirées", icon: PartyPopper },
    { k: "menu", label: "Menu (OCR)", icon: ScanText },
    { k: "gallery", label: "Galerie", icon: ImagePlus },
    { k: "content", label: "Textes du site", icon: FileText },
    { k: "social", label: "FB / Instagram", icon: Send },
    { k: "messages", label: "Messages", icon: MessageSquare },
  ];

  return (
    <div data-testid="admin-page" className="pt-28 pb-20">
      <div className="max-w-[1440px] mx-auto px-6 md:px-10">
        <div className="flex flex-wrap items-center justify-between gap-4 mb-8">
          <div>
            <div className="eyebrow">Painel de controlo</div>
            <h1 className="text-4xl md:text-5xl mt-2">Tableau de bord</h1>
          </div>
          <button onClick={logout} className="btn-ghost" data-testid="admin-logout-btn"><LogOut size={16} className="mr-2" /> Déconnexion</button>
        </div>

        <div className="flex gap-1 border-b border-[#E6E1D6] mb-8 overflow-x-auto no-scrollbar">
          {tabs.map((t) => (
            <button key={t.k} onClick={() => setTab(t.k)} data-testid={`admin-tab-${t.k}`}
              className={`px-5 py-3 text-sm font-medium border-b-2 transition whitespace-nowrap inline-flex items-center gap-2 ${
                tab === t.k ? "border-[#C04E38] text-[#C04E38]" : "border-transparent text-[#5C5A56] hover:text-[#2C2B29]"
              }`}>
              <t.icon size={16}/> {t.label}
            </button>
          ))}
        </div>

        {tab === "dashboard" && <DashboardPanel code={code} setTab={setTab} />}
        {tab === "reservations" && <ReservationsPanel code={code} />}
        {tab === "privatizations" && <PrivatizationsPanel code={code} />}
        {tab === "events" && <EventsPanel code={code} />}
        {tab === "menu" && <MenuPanel code={code} />}
        {tab === "gallery" && <GalleryPanel code={code} />}
        {tab === "content" && <ContentPanel code={code} />}
        {tab === "social" && <SocialPanel code={code} />}
        {tab === "messages" && <MessagesPanel code={code} />}
      </div>
    </div>
  );
}

// ============ DASHBOARD ============
function DashboardPanel({ code, setTab }) {
  const [stats, setStats] = useState(null);
  const [config, setConfig] = useState(null);
  useEffect(() => {
    adminAxios(code).get("/admin/stats").then(r => setStats(r.data));
    adminAxios(code).get("/admin/social/config").then(r => setConfig(r.data));
  }, [code]);
  if (!stats) return <p className="text-[#5C5A56]">Chargement…</p>;
  const cards = [
    { label: "Réservations totales", value: stats.reservations.total, color: "#C04E38", tab: "reservations" },
    { label: "À traiter (en attente)", value: stats.reservations.pending, color: "#1F476B", tab: "reservations" },
    { label: "À venir (confirmées)", value: stats.reservations.upcoming, sub: `${stats.reservations.upcoming_guests} couverts`, color: "#2C2B29", tab: "reservations" },
    { label: "Privatisations", value: stats.privatizations.total, sub: `${stats.privatizations.pending} à traiter`, color: "#C04E38", tab: "privatizations" },
    { label: "Messages reçus", value: stats.messages, color: "#1F476B", tab: "messages" },
  ];
  return (
    <div data-testid="dashboard-panel" className="space-y-8">
      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-4">
        {cards.map((c, i) => (
          <button key={i} onClick={() => setTab(c.tab)} className="card-warm p-5 text-left hover:shadow-lg transition" data-testid={`dash-card-${i}`}>
            <div className="text-xs uppercase tracking-widest text-[#5C5A56]">{c.label}</div>
            <div className="font-serif-display text-4xl mt-2" style={{color: c.color}}>{c.value}</div>
            {c.sub && <div className="text-xs text-[#5C5A56] mt-1">{c.sub}</div>}
          </button>
        ))}
      </div>
      <div className="grid md:grid-cols-3 gap-4">
        <div className={`card-warm p-5 ${config?.gmail_configured ? "" : "bg-amber-50 border-amber-200"}`}>
          <div className="flex items-center justify-between mb-2"><span className="font-medium">Gmail SMTP</span>
            {config?.gmail_configured ? <Check className="text-green-600" size={18}/> : <X className="text-amber-600" size={18}/>}</div>
          <p className="text-xs text-[#5C5A56]">{config?.gmail_configured ? "Activé — emails envoyés" : "Non configuré (mode test, emails loggés)"}</p>
        </div>
        <div className={`card-warm p-5 ${config?.facebook_configured ? "" : "bg-amber-50 border-amber-200"}`}>
          <div className="flex items-center justify-between mb-2"><span className="font-medium"><Facebook size={14} className="inline mr-1"/> Facebook</span>
            {config?.facebook_configured ? <Check className="text-green-600" size={18}/> : <X className="text-amber-600" size={18}/>}</div>
          <p className="text-xs text-[#5C5A56]">{config?.facebook_configured ? "Page connectée" : "Token Page FB manquant dans .env"}</p>
        </div>
        <div className={`card-warm p-5 ${config?.instagram_configured ? "" : "bg-amber-50 border-amber-200"}`}>
          <div className="flex items-center justify-between mb-2"><span className="font-medium"><Instagram size={14} className="inline mr-1"/> Instagram</span>
            {config?.instagram_configured ? <Check className="text-green-600" size={18}/> : <X className="text-amber-600" size={18}/>}</div>
          <p className="text-xs text-[#5C5A56]">{config?.instagram_configured ? "Compte Business lié" : "IG Business ID manquant dans .env"}</p>
        </div>
      </div>
    </div>
  );
}

// ============ RESERVATIONS ============
function ReservationsPanel({ code }) {
  const [list, setList] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState("all");
  const [search, setSearch] = useState("");

  const refresh = () => {
    setLoading(true);
    adminAxios(code).get("/admin/reservations")
      .then((r) => setList(r.data || []))
      .catch(() => toast.error("Erreur de chargement"))
      .finally(() => setLoading(false));
  };
  useEffect(() => { refresh(); }, []);

  const updateStatus = async (id, status) => {
    try { await adminAxios(code).patch(`/admin/reservations/${id}`, { status }); toast.success("Mis à jour"); refresh(); }
    catch { toast.error("Erreur"); }
  };

  const filtered = list.filter((r) => {
    if (filter !== "all" && r.status !== filter) return false;
    if (search) {
      const q = search.toLowerCase();
      return r.name.toLowerCase().includes(q) || r.email.toLowerCase().includes(q) || r.phone.includes(q) || r.date.includes(q);
    }
    return true;
  });

  return (
    <div data-testid="reservations-panel">
      <div className="flex flex-wrap gap-3 mb-5 items-center">
        <div className="flex gap-2">
          {[
            { k: "all", label: `Toutes (${list.length})` },
            { k: "pending", label: `En attente (${list.filter(r=>r.status==="pending").length})` },
            { k: "confirmed", label: `Confirmées (${list.filter(r=>r.status==="confirmed").length})` },
            { k: "cancelled", label: `Annulées (${list.filter(r=>r.status==="cancelled").length})` },
          ].map((f) => (
            <button key={f.k} onClick={() => setFilter(f.k)} data-testid={`reservation-filter-${f.k}`}
              className={`px-3 py-1.5 rounded-full text-sm border transition ${
                filter === f.k ? "bg-[#1F476B] text-white border-[#1F476B]" : "bg-white border-[#E6E1D6] text-[#2C2B29]"
              }`}>{f.label}</button>
          ))}
        </div>
        <input className="input-warm !py-2 max-w-xs ml-auto" placeholder="Rechercher (nom, email, date…)"
          value={search} onChange={(e)=>setSearch(e.target.value)} data-testid="reservation-search"/>
        <button onClick={refresh} className="btn-ghost" data-testid="reservation-refresh"><RefreshCw size={14} className="mr-2" /> Actualiser</button>
      </div>

      {loading ? <p className="text-[#5C5A56]">Chargement…</p> : filtered.length === 0 ? (
        <p className="text-[#5C5A56]" data-testid="reservations-empty">Aucune réservation.</p>
      ) : (
        <div className="overflow-x-auto card-warm">
          <table className="w-full text-sm">
            <thead className="bg-[#F4F1EA] text-left text-xs uppercase tracking-wider text-[#5C5A56]">
              <tr>
                <th className="px-4 py-3">Date / heure</th><th className="px-4 py-3">Client</th>
                <th className="px-4 py-3">Contact</th><th className="px-4 py-3">Couverts</th>
                <th className="px-4 py-3">Occasion</th><th className="px-4 py-3">Statut</th><th className="px-4 py-3">Actions</th>
              </tr>
            </thead>
            <tbody>
              {filtered.map((r) => (
                <tr key={r.id} className="border-t border-[#E6E1D6]" data-testid={`reservation-row-${r.id}`}>
                  <td className="px-4 py-3 font-medium">{r.date}<br/><span className="text-xs text-[#5C5A56]">{r.time}</span></td>
                  <td className="px-4 py-3">{r.name}</td>
                  <td className="px-4 py-3"><div className="text-xs">{r.email}</div><div className="text-xs"><a href={`tel:${r.phone}`} className="text-[#1F476B]">{r.phone}</a></div></td>
                  <td className="px-4 py-3 text-center">{r.guests}</td>
                  <td className="px-4 py-3 max-w-xs">{r.occasion && <div className="text-xs uppercase tracking-wider text-[#C04E38]">{r.occasion}</div>}{r.message && <div className="text-xs text-[#5C5A56] line-clamp-2">{r.message}</div>}</td>
                  <td className="px-4 py-3"><StatusPill s={r.status}/></td>
                  <td className="px-4 py-3">
                    <div className="flex gap-1">
                      {r.status !== "confirmed" && <button onClick={() => updateStatus(r.id, "confirmed")} className="p-2 rounded-lg bg-green-50 text-green-700 hover:bg-green-100" title="Confirmer" data-testid={`confirm-${r.id}`}><Check size={14}/></button>}
                      {r.status !== "cancelled" && <button onClick={() => updateStatus(r.id, "cancelled")} className="p-2 rounded-lg bg-red-50 text-red-700 hover:bg-red-100" title="Annuler" data-testid={`cancel-${r.id}`}><X size={14}/></button>}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

function StatusPill({ s }) {
  const cls = s === "confirmed" ? "bg-green-100 text-green-700" : s === "cancelled" ? "bg-red-100 text-red-700" : "bg-amber-100 text-amber-700";
  const label = s === "confirmed" ? "Confirmée" : s === "cancelled" ? "Annulée" : "En attente";
  return <span className={`inline-block px-2.5 py-1 rounded-full text-xs ${cls}`}>{label}</span>;
}

// ============ PRIVATIZATIONS ============
function PrivatizationsPanel({ code }) {
  const [list, setList] = useState([]); const [loading, setLoading] = useState(true);
  const refresh = () => { setLoading(true); adminAxios(code).get("/admin/privatizations").then(r=>setList(r.data||[])).finally(()=>setLoading(false)); };
  useEffect(() => { refresh(); }, []);
  const update = async (id, status) => { try { await adminAxios(code).patch(`/admin/privatizations/${id}`, { status }); toast.success("Mis à jour"); refresh(); } catch { toast.error("Erreur"); } };

  if (loading) return <p className="text-[#5C5A56]">Chargement…</p>;
  if (list.length === 0) return <p className="text-[#5C5A56]" data-testid="privatizations-empty">Aucune demande de privatisation.</p>;
  return (
    <div data-testid="privatizations-panel" className="grid md:grid-cols-2 gap-4">
      {list.map((p) => (
        <div key={p.id} className="card-warm p-6" data-testid={`priv-${p.id}`}>
          <div className="flex justify-between items-start mb-3">
            <div>
              <div className="text-xs uppercase tracking-widest text-[#C04E38]">{p.event_type}</div>
              <h3 className="text-xl font-serif-display mt-1">{p.name}</h3>
            </div>
            <StatusPill s={p.status}/>
          </div>
          <div className="text-sm space-y-1">
            <div><b>Date :</b> {p.date} · <b>{p.guests} pers.</b></div>
            <div className="text-xs text-[#5C5A56]"><a href={`mailto:${p.email}`} className="text-[#1F476B]">{p.email}</a> · <a href={`tel:${p.phone}`} className="text-[#1F476B]">{p.phone}</a></div>
            {p.budget && <div className="text-xs"><b>Budget :</b> {p.budget}</div>}
            {p.needs?.length > 0 && <div className="text-xs"><b>Besoins :</b> {p.needs.join(", ")}</div>}
            {p.message && <p className="text-xs text-[#5C5A56] mt-2 italic">"{p.message}"</p>}
          </div>
          <div className="flex gap-2 mt-4">
            {p.status !== "confirmed" && <button onClick={()=>update(p.id, "confirmed")} className="px-3 py-1.5 text-xs rounded-lg bg-green-50 text-green-700 hover:bg-green-100"><Check size={12} className="inline mr-1"/> Confirmer</button>}
            {p.status !== "cancelled" && <button onClick={()=>update(p.id, "cancelled")} className="px-3 py-1.5 text-xs rounded-lg bg-red-50 text-red-700 hover:bg-red-100"><X size={12} className="inline mr-1"/> Annuler</button>}
            <a href={`tel:${p.phone}`} className="px-3 py-1.5 text-xs rounded-lg bg-[#1F476B]/10 text-[#1F476B] hover:bg-[#1F476B]/20"><Phone size={12} className="inline mr-1"/> Rappeler</a>
          </div>
        </div>
      ))}
    </div>
  );
}

// ============ MENU PANEL (with edit + reorder) ============
function MenuPanel({ code }) {
  const [items, setItems] = useState([]);
  const [extracting, setExtracting] = useState(false);
  const [extracted, setExtracted] = useState([]);
  const [saving, setSaving] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [editForm, setEditForm] = useState({});
  const fileRef = useRef(null);

  const refresh = () => api.get("/menu").then((r) => setItems(r.data || []));
  useEffect(() => { refresh(); }, []);

  const onUpload = async (e) => {
    const file = e.target.files?.[0]; if (!file) return;
    if (!["image/jpeg","image/png","image/webp","image/jpg"].includes(file.type)) { toast.error("JPEG/PNG/WEBP requis"); return; }
    const fd = new FormData(); fd.append("file", file);
    setExtracting(true); setExtracted([]);
    try {
      const res = await adminAxios(code).post("/admin/menu/ocr", fd, { headers: { "Content-Type": "multipart/form-data" } });
      const found = res.data?.items || [];
      setExtracted(found);
      toast.success(found.length ? `${found.length} plats détectés !` : "Aucun plat détecté");
    } catch (err) { toast.error(err?.response?.data?.detail || "Erreur OCR"); }
    finally { setExtracting(false); e.target.value = ""; }
  };

  const updateExtracted = (i, k, v) => { const c = [...extracted]; c[i] = { ...c[i], [k]: v }; setExtracted(c); };
  const removeExtracted = (i) => setExtracted(extracted.filter((_, idx) => idx !== i));

  const saveAll = async () => {
    if (extracted.length === 0) return;
    setSaving(true);
    try { await adminAxios(code).post("/admin/menu/items", { items: extracted }); toast.success("Plats ajoutés !"); setExtracted([]); refresh(); }
    catch (err) { toast.error(err?.response?.data?.detail || "Erreur"); }
    finally { setSaving(false); }
  };

  const startEdit = (it) => { setEditingId(it.id); setEditForm({ name: it.name, description: it.description || "", price: it.price || "", category: it.category, featured: it.featured }); };
  const cancelEdit = () => { setEditingId(null); setEditForm({}); };
  const saveEdit = async (id) => {
    try { await adminAxios(code).patch(`/admin/menu/items/${id}`, editForm); toast.success("Plat mis à jour"); setEditingId(null); refresh(); }
    catch { toast.error("Erreur"); }
  };

  const removeItem = async (id) => {
    if (!window.confirm("Supprimer ce plat ?")) return;
    try { await adminAxios(code).delete(`/admin/menu/items/${id}`); toast.success("Supprimé"); refresh(); } catch { toast.error("Erreur"); }
  };

  const moveItem = async (cat, idx, dir) => {
    const catItems = items.filter(i => i.category === cat).sort((a,b) => (a.position||0)-(b.position||0));
    const target = idx + dir;
    if (target < 0 || target >= catItems.length) return;
    const [a, b] = [catItems[idx], catItems[target]];
    const reorder = catItems.map((it, i) => {
      if (i === idx) return { id: a.id, position: target };
      if (i === target) return { id: b.id, position: idx };
      return { id: it.id, position: i };
    });
    try { await adminAxios(code).post("/admin/menu/reorder", { items: reorder }); refresh(); } catch { toast.error("Erreur"); }
  };

  const toggleFeatured = async (it) => {
    try { await adminAxios(code).patch(`/admin/menu/items/${it.id}`, { featured: !it.featured }); refresh(); } catch { toast.error("Erreur"); }
  };

  return (
    <div data-testid="menu-panel" className="space-y-10">
      {/* OCR Card */}
      <div className="card-warm p-6 md:p-8 bg-gradient-to-br from-[#F4F1EA] to-white">
        <div className="flex items-start gap-4 mb-4">
          <div className="w-12 h-12 rounded-full bg-[#C04E38] text-white grid place-items-center shrink-0"><ScanText size={22}/></div>
          <div>
            <h3 className="text-2xl">Scanner un menu papier</h3>
            <p className="text-sm text-[#5C5A56] mt-1">Photographiez votre carte papier — l'IA Gemini Vision extrait nom, description, prix et catégorie automatiquement.</p>
          </div>
        </div>
        <input ref={fileRef} type="file" accept="image/png,image/jpeg,image/webp" className="hidden" onChange={onUpload} data-testid="ocr-file-input"/>
        <button onClick={() => fileRef.current?.click()} disabled={extracting} className="btn-primary" data-testid="ocr-upload-btn">
          {extracting ? <><Loader2 className="animate-spin mr-2" size={18}/> Analyse…</> : <><Upload size={18} className="mr-2"/> Choisir une image</>}
        </button>
        {extracted.length > 0 && (
          <div className="mt-8" data-testid="ocr-extracted-list">
            <div className="flex items-center justify-between mb-4">
              <h4 className="text-lg font-serif-display">Plats détectés ({extracted.length}) — corrigez avant ajout</h4>
              <button onClick={saveAll} disabled={saving} className="btn-primary !py-2.5" data-testid="ocr-save-all">
                {saving ? <Loader2 className="animate-spin mr-2" size={16}/> : null} Ajouter tout au menu
              </button>
            </div>
            <div className="space-y-2">
              {extracted.map((it, i) => (
                <div key={i} className="grid md:grid-cols-12 gap-2 items-start bg-white rounded-xl p-3 border border-[#E6E1D6]" data-testid={`extracted-${i}`}>
                  <input className="input-warm md:col-span-3 !py-2" value={it.name} onChange={(e)=>updateExtracted(i,"name",e.target.value)} placeholder="Nom"/>
                  <input className="input-warm md:col-span-5 !py-2" value={it.description||""} onChange={(e)=>updateExtracted(i,"description",e.target.value)} placeholder="Description"/>
                  <input className="input-warm md:col-span-2 !py-2" value={it.price||""} onChange={(e)=>updateExtracted(i,"price",e.target.value)} placeholder="Prix"/>
                  <select className="input-warm md:col-span-1 !py-2 text-xs" value={it.category} onChange={(e)=>updateExtracted(i,"category",e.target.value)}>
                    {CATEGORY_ORDER.map(c => <option key={c} value={c}>{CATEGORY_LABELS[c]}</option>)}
                  </select>
                  <button onClick={()=>removeExtracted(i)} className="p-2 rounded-lg bg-red-50 text-red-700 hover:bg-red-100 md:col-span-1 justify-self-end"><Trash2 size={14}/></button>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>

      {/* Existing menu — editable + reorderable */}
      <div>
        <h3 className="text-2xl mb-5">Carte actuelle ({items.length} plats) — utilisez les flèches pour réorganiser</h3>
        {CATEGORY_ORDER.map((cat) => {
          const cItems = items.filter((i) => i.category === cat).sort((a,b) => (a.position||0)-(b.position||0));
          if (cItems.length === 0) return null;
          return (
            <div key={cat} className="mb-8" data-testid={`admin-cat-${cat}`}>
              <h4 className="font-serif-display text-xl mb-3 text-[#1F476B]">{CATEGORY_LABELS[cat]} ({cItems.length})</h4>
              <div className="space-y-2">
                {cItems.map((it, idx) => editingId === it.id ? (
                  <div key={it.id} className="card-warm p-4 grid md:grid-cols-12 gap-2" data-testid={`edit-${it.id}`}>
                    <input className="input-warm md:col-span-3 !py-2" value={editForm.name} onChange={(e)=>setEditForm({...editForm, name: e.target.value})} placeholder="Nom"/>
                    <input className="input-warm md:col-span-4 !py-2" value={editForm.description} onChange={(e)=>setEditForm({...editForm, description: e.target.value})} placeholder="Description"/>
                    <input className="input-warm md:col-span-2 !py-2" value={editForm.price} onChange={(e)=>setEditForm({...editForm, price: e.target.value})} placeholder="Prix"/>
                    <select className="input-warm md:col-span-2 !py-2 text-xs" value={editForm.category} onChange={(e)=>setEditForm({...editForm, category: e.target.value})}>
                      {CATEGORY_ORDER.map(c => <option key={c} value={c}>{CATEGORY_LABELS[c]}</option>)}
                    </select>
                    <div className="md:col-span-1 flex gap-1">
                      <button onClick={()=>saveEdit(it.id)} className="p-2 rounded-lg bg-green-50 text-green-700 hover:bg-green-100"><Save size={14}/></button>
                      <button onClick={cancelEdit} className="p-2 rounded-lg bg-gray-100 hover:bg-gray-200"><X size={14}/></button>
                    </div>
                  </div>
                ) : (
                  <div key={it.id} className="card-warm p-4 flex gap-3 items-start" data-testid={`admin-item-${it.id}`}>
                    <div className="flex flex-col gap-0.5 shrink-0">
                      <button onClick={()=>moveItem(cat, idx, -1)} disabled={idx === 0} className="p-1 rounded hover:bg-[#F4F1EA] disabled:opacity-30" data-testid={`move-up-${it.id}`}><ChevronUp size={16}/></button>
                      <button onClick={()=>moveItem(cat, idx, 1)} disabled={idx === cItems.length-1} className="p-1 rounded hover:bg-[#F4F1EA] disabled:opacity-30" data-testid={`move-down-${it.id}`}><ChevronDown size={16}/></button>
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 flex-wrap">
                        <span className="font-medium truncate">{it.name}</span>
                        {it.price && <span className="text-sm text-[#C04E38]">{it.price}</span>}
                        {it.featured && <Star size={12} fill="#C04E38" className="text-[#C04E38]"/>}
                      </div>
                      {it.description && <div className="text-xs text-[#5C5A56] line-clamp-2 mt-1">{it.description}</div>}
                    </div>
                    <div className="flex gap-1 shrink-0">
                      <button onClick={()=>toggleFeatured(it)} className={`p-2 rounded-lg ${it.featured ? "bg-[#C04E38]/10 text-[#C04E38]" : "bg-gray-50 text-gray-500"} hover:bg-[#C04E38]/20`} title="Mettre en vedette" data-testid={`featured-${it.id}`}><Star size={14} fill={it.featured ? "#C04E38" : "none"}/></button>
                      <button onClick={()=>startEdit(it)} className="p-2 rounded-lg bg-blue-50 text-blue-700 hover:bg-blue-100" title="Éditer" data-testid={`edit-btn-${it.id}`}><Edit3 size={14}/></button>
                      <button onClick={()=>removeItem(it.id)} className="p-2 rounded-lg bg-red-50 text-red-700 hover:bg-red-100" title="Supprimer" data-testid={`delete-item-${it.id}`}><Trash2 size={14}/></button>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}

// ============ SOCIAL PANEL ============
function SocialPanel({ code }) {
  const [config, setConfig] = useState(null);
  const [caption, setCaption] = useState("🍽️ Le menu de la semaine au Globo Dancing !\n\n");
  const [postFB, setPostFB] = useState(true);
  const [postIG, setPostIG] = useState(true);
  const [file, setFile] = useState(null);
  const [preview, setPreview] = useState(null);
  const [posting, setPosting] = useState(false);
  const [history, setHistory] = useState([]);
  const fileRef = useRef(null);

  useEffect(() => {
    adminAxios(code).get("/admin/social/config").then(r => setConfig(r.data));
    adminAxios(code).get("/admin/social/posts").then(r => setHistory(r.data || []));
  }, [code]);

  const onPickFile = (e) => {
    const f = e.target.files?.[0]; if (!f) return;
    if (!["image/jpeg","image/png","image/webp","image/jpg"].includes(f.type)) { toast.error("JPEG/PNG/WEBP requis"); return; }
    setFile(f);
    setPreview(URL.createObjectURL(f));
  };

  const submit = async () => {
    if (!file) { toast.error("Choisissez une image"); return; }
    if (!caption.trim()) { toast.error("Légende vide"); return; }
    if (!postFB && !postIG) { toast.error("Sélectionnez au moins une plateforme"); return; }
    setPosting(true);
    const fd = new FormData();
    fd.append("file", file);
    fd.append("caption", caption);
    fd.append("post_to_facebook", String(postFB));
    fd.append("post_to_instagram", String(postIG));
    try {
      const res = await adminAxios(code).post("/admin/social/post", fd, { headers: { "Content-Type": "multipart/form-data" }, timeout: 90000 });
      const d = res.data;
      if (d.success) {
        toast.success("Publié !");
      } else {
        toast.error("Échec de publication — voir détails");
      }
      if (d.errors) {
        Object.entries(d.errors).forEach(([k, v]) => toast.error(`${k}: ${v}`));
      }
      // Refresh history
      adminAxios(code).get("/admin/social/posts").then(r => setHistory(r.data || []));
      if (d.success) { setFile(null); setPreview(null); setCaption("🍽️ Le menu de la semaine au Globo Dancing !\n\n"); }
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Erreur");
    } finally { setPosting(false); }
  };

  return (
    <div data-testid="social-panel" className="space-y-8">
      {(!config?.facebook_configured || !config?.instagram_configured) && (
        <div className="card-warm p-5 bg-amber-50 border-amber-200">
          <h4 className="font-medium mb-2">⚠️ Configuration sociale incomplète</h4>
          <ul className="text-sm text-[#5C5A56] space-y-1">
            {!config?.facebook_configured && <li>• Facebook : ajoutez <code>FB_PAGE_ID</code> et <code>FB_PAGE_ACCESS_TOKEN</code> dans <code>/app/backend/.env</code></li>}
            {!config?.instagram_configured && <li>• Instagram : ajoutez <code>IG_BUSINESS_ACCOUNT_ID</code> dans <code>.env</code> (compte Business lié à votre Page FB)</li>}
            <li className="text-xs mt-2">Une fois configuré, redémarrez le backend.</li>
          </ul>
        </div>
      )}

      <div className="card-warm p-6 md:p-8">
        <h3 className="text-2xl mb-2">Publier sur les réseaux</h3>
        <p className="text-sm text-[#5C5A56] mb-5">Postez simultanément sur votre Page Facebook et votre compte Instagram Business.</p>

        <div className="grid lg:grid-cols-2 gap-6">
          <div className="space-y-4">
            <div>
              <label className="text-sm font-medium mb-1.5 block">Image (menu de la semaine, plat, événement…)</label>
              <input ref={fileRef} type="file" accept="image/png,image/jpeg,image/webp" className="hidden" onChange={onPickFile} data-testid="social-file-input"/>
              <button onClick={() => fileRef.current?.click()} className="btn-secondary" data-testid="social-pick-image">
                <ImageIcon size={16} className="mr-2"/> {file ? "Changer l'image" : "Choisir une image"}
              </button>
              {preview && <img src={preview} alt="aperçu" className="mt-3 rounded-xl max-h-72 border border-[#E6E1D6]"/>}
            </div>

            <div>
              <label className="text-sm font-medium mb-1.5 block">Légende</label>
              <textarea rows={6} className="input-warm font-mono text-sm" value={caption} onChange={(e)=>setCaption(e.target.value)} data-testid="social-caption" placeholder="Le menu de la semaine au Globo Dancing…"/>
              <div className="text-xs text-[#5C5A56] mt-1">{caption.length} caractères (max IG : 2200)</div>
            </div>

            <div className="flex gap-3">
              <label className={`flex items-center gap-2 px-4 py-2.5 rounded-full text-sm border-2 cursor-pointer transition ${postFB ? "border-[#1877F2] bg-[#1877F2]/5" : "border-[#E6E1D6]"} ${!config?.facebook_configured ? "opacity-50" : ""}`}>
                <input type="checkbox" checked={postFB} onChange={(e)=>setPostFB(e.target.checked)} className="hidden" disabled={!config?.facebook_configured} data-testid="toggle-fb"/>
                <Facebook size={16}/> Facebook
              </label>
              <label className={`flex items-center gap-2 px-4 py-2.5 rounded-full text-sm border-2 cursor-pointer transition ${postIG ? "border-[#E1306C] bg-[#E1306C]/5" : "border-[#E6E1D6]"} ${!config?.instagram_configured ? "opacity-50" : ""}`}>
                <input type="checkbox" checked={postIG} onChange={(e)=>setPostIG(e.target.checked)} className="hidden" disabled={!config?.instagram_configured} data-testid="toggle-ig"/>
                <Instagram size={16}/> Instagram
              </label>
            </div>

            <button onClick={submit} disabled={posting || !file} className="btn-primary" data-testid="social-publish-btn">
              {posting ? <><Loader2 className="animate-spin mr-2" size={18}/> Publication…</> : <><Send size={16} className="mr-2"/> Publier maintenant</>}
            </button>
          </div>

          {/* Phone preview */}
          <div className="bg-gradient-to-br from-[#F4F1EA] to-white rounded-2xl p-6 border border-[#E6E1D6]">
            <div className="text-xs uppercase tracking-widest text-[#5C5A56] mb-3">Aperçu</div>
            <div className="bg-white rounded-xl border border-[#E6E1D6] overflow-hidden max-w-xs mx-auto shadow">
              <div className="flex items-center gap-2 p-3 border-b border-[#E6E1D6]">
                <div className="w-8 h-8 rounded-full bg-[#C04E38] grid place-items-center text-white text-xs font-serif-display">G</div>
                <div className="text-sm"><b>Globo Dancing</b><div className="text-xs text-[#5C5A56]">Trappes</div></div>
              </div>
              {preview ? <img src={preview} alt="" className="w-full aspect-square object-cover"/> : <div className="aspect-square bg-[#F4F1EA] grid place-items-center text-[#5C5A56] text-xs">Aperçu de l'image</div>}
              <div className="p-3 text-xs whitespace-pre-wrap line-clamp-6">{caption}</div>
            </div>
          </div>
        </div>
      </div>

      {/* History */}
      {history.length > 0 && (
        <div>
          <h3 className="text-2xl mb-4">Historique des publications</h3>
          <div className="space-y-3">
            {history.slice(0, 10).map((h) => (
              <div key={h.id} className="card-warm p-4 flex gap-4 items-start" data-testid={`social-history-${h.id}`}>
                {h.image_url && <img src={h.image_url} alt="" className="w-20 h-20 rounded-lg object-cover shrink-0"/>}
                <div className="flex-1 min-w-0">
                  <div className="text-xs text-[#5C5A56]">{new Date(h.created_at).toLocaleString("fr-FR")}</div>
                  <p className="text-sm line-clamp-2 mt-1">{h.caption}</p>
                  <div className="flex gap-2 mt-2 flex-wrap">
                    {h.facebook_success ? <a href={h.facebook_post_url} target="_blank" rel="noreferrer" className="text-xs px-2 py-1 rounded bg-[#1877F2]/10 text-[#1877F2]"><Facebook size={10} className="inline mr-1"/> Voir sur FB</a> : h.errors?.facebook && <span className="text-xs px-2 py-1 rounded bg-red-50 text-red-600">FB échec</span>}
                    {h.instagram_success ? <a href={h.instagram_post_url} target="_blank" rel="noreferrer" className="text-xs px-2 py-1 rounded bg-[#E1306C]/10 text-[#E1306C]"><Instagram size={10} className="inline mr-1"/> Voir sur IG</a> : h.errors?.instagram && <span className="text-xs px-2 py-1 rounded bg-red-50 text-red-600">IG échec</span>}
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

// ============ MESSAGES ============
function MessagesPanel({ code }) {
  const [list, setList] = useState([]); const [loading, setLoading] = useState(true);
  useEffect(() => { adminAxios(code).get("/admin/contact").then((r) => setList(r.data || [])).finally(() => setLoading(false)); }, [code]);
  if (loading) return <p className="text-[#5C5A56]">Chargement…</p>;
  if (list.length === 0) return <p className="text-[#5C5A56]" data-testid="messages-empty">Aucun message reçu.</p>;
  return (
    <div data-testid="messages-panel" className="space-y-4">
      {list.map((m) => (
        <div key={m.id} className="card-warm p-5" data-testid={`message-${m.id}`}>
          <div className="flex justify-between gap-4 flex-wrap">
            <div><div className="font-medium">{m.name} · <span className="text-[#5C5A56] font-normal text-sm">{m.email}</span></div>
              {m.subject && <div className="text-xs uppercase tracking-wider text-[#C04E38] mt-1">{m.subject}</div>}</div>
            <div className="text-xs text-[#5C5A56]">{new Date(m.created_at).toLocaleString("fr-FR")}</div>
          </div>
          <p className="text-sm text-[#2C2B29] mt-3 leading-relaxed whitespace-pre-wrap">{m.message}</p>
        </div>
      ))}
    </div>
  );
}


// ============ GALLERY PANEL ============
function GalleryPanel({ code }) {
  const [photos, setPhotos] = useState([]);
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);
  const [editAlt, setEditAlt] = useState({});
  const fileRef = useRef(null);

  const refresh = () => {
    setLoading(true);
    api.get("/gallery").then(r => setPhotos((r.data || []).sort((a,b) => (a.position||0)-(b.position||0)))).finally(() => setLoading(false));
  };
  useEffect(() => { refresh(); }, []);

  const onUpload = async (e) => {
    const files = Array.from(e.target.files || []);
    if (files.length === 0) return;
    setUploading(true);
    try {
      for (const f of files) {
        if (!["image/jpeg","image/png","image/webp","image/jpg"].includes(f.type)) {
          toast.error(`${f.name} : JPEG/PNG/WEBP requis`);
          continue;
        }
        if (f.size > 8 * 1024 * 1024) {
          toast.error(`${f.name} : max 8 Mo`);
          continue;
        }
        const fd = new FormData();
        fd.append("file", f);
        fd.append("alt", "");
        await adminAxios(code).post("/admin/gallery/upload", fd, {
          headers: { "Content-Type": "multipart/form-data" },
          timeout: 60000,
        });
      }
      toast.success(`${files.length} photo(s) ajoutée(s) !`);
      refresh();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Erreur upload");
    } finally {
      setUploading(false);
      e.target.value = "";
    }
  };

  const move = async (idx, dir) => {
    const target = idx + dir;
    if (target < 0 || target >= photos.length) return;
    const reordered = [...photos];
    [reordered[idx], reordered[target]] = [reordered[target], reordered[idx]];
    setPhotos(reordered);
    try {
      await adminAxios(code).post("/admin/gallery/reorder", { ids: reordered.map(p => p.id) });
    } catch { toast.error("Erreur"); refresh(); }
  };

  const saveAlt = async (id) => {
    const alt = editAlt[id];
    if (alt === undefined) return;
    try {
      await adminAxios(code).patch(`/admin/gallery/${id}`, { alt });
      toast.success("Légende mise à jour");
      setEditAlt(prev => { const n = {...prev}; delete n[id]; return n; });
      refresh();
    } catch { toast.error("Erreur"); }
  };

  const remove = async (id) => {
    if (!window.confirm("Supprimer cette photo ?")) return;
    try { await adminAxios(code).delete(`/admin/gallery/${id}`); toast.success("Supprimée"); refresh(); }
    catch { toast.error("Erreur"); }
  };

  return (
    <div data-testid="gallery-panel" className="space-y-6">
      <div className="card-warm p-6 md:p-8 bg-gradient-to-br from-[#F4F1EA] to-white">
        <div className="flex items-start gap-4 mb-4">
          <div className="w-12 h-12 rounded-full bg-[#C04E38] text-white grid place-items-center shrink-0"><ImagePlus size={22}/></div>
          <div>
            <h3 className="text-2xl">Photos de la galerie</h3>
            <p className="text-sm text-[#5C5A56] mt-1">Uploadez vos photos (plusieurs à la fois), réorganisez par les flèches, éditez les légendes. Affichées automatiquement sur la page Galerie.</p>
          </div>
        </div>
        <input ref={fileRef} type="file" accept="image/png,image/jpeg,image/webp" multiple className="hidden" onChange={onUpload} data-testid="gallery-file-input"/>
        <button onClick={() => fileRef.current?.click()} disabled={uploading} className="btn-primary" data-testid="gallery-upload-btn">
          {uploading ? <><Loader2 className="animate-spin mr-2" size={18}/> Upload…</> : <><Upload size={18} className="mr-2"/> Ajouter des photos</>}
        </button>
      </div>

      {loading ? <p className="text-[#5C5A56]">Chargement…</p> : photos.length === 0 ? (
        <p className="text-[#5C5A56]" data-testid="gallery-panel-empty">Aucune photo. Uploadez-en pour commencer !</p>
      ) : (
        <div>
          <p className="text-sm text-[#5C5A56] mb-4">{photos.length} photo(s) — ordre d'affichage sur le site</p>
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
            {photos.map((p, idx) => (
              <div key={p.id} className="card-warm overflow-hidden" data-testid={`gallery-photo-${p.id}`}>
                <div className="aspect-[4/3] overflow-hidden">
                  <img src={p.url} alt={p.alt || ""} className="w-full h-full object-cover"/>
                </div>
                <div className="p-3 space-y-2">
                  <div className="flex items-center justify-between">
                    <span className="text-xs text-[#5C5A56]">#{idx + 1}</span>
                    <div className="flex gap-1">
                      <button onClick={() => move(idx, -1)} disabled={idx === 0} className="p-1.5 rounded hover:bg-[#F4F1EA] disabled:opacity-30" data-testid={`gallery-up-${p.id}`}><ChevronUp size={14}/></button>
                      <button onClick={() => move(idx, 1)} disabled={idx === photos.length - 1} className="p-1.5 rounded hover:bg-[#F4F1EA] disabled:opacity-30" data-testid={`gallery-down-${p.id}`}><ChevronDown size={14}/></button>
                      <button onClick={() => remove(p.id)} className="p-1.5 rounded bg-red-50 text-red-700 hover:bg-red-100" data-testid={`gallery-delete-${p.id}`}><Trash2 size={14}/></button>
                    </div>
                  </div>
                  <div className="flex gap-2">
                    <input
                      className="input-warm !py-1.5 !px-2 text-xs flex-1"
                      placeholder="Légende / alt (SEO)"
                      value={editAlt[p.id] !== undefined ? editAlt[p.id] : (p.alt || "")}
                      onChange={(e) => setEditAlt({ ...editAlt, [p.id]: e.target.value })}
                      data-testid={`gallery-alt-${p.id}`}
                    />
                    {editAlt[p.id] !== undefined && editAlt[p.id] !== (p.alt || "") && (
                      <button onClick={() => saveAlt(p.id)} className="p-1.5 rounded bg-green-50 text-green-700 hover:bg-green-100"><Save size={14}/></button>
                    )}
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

// ============ CONTENT PANEL ============
const CONTENT_FIELDS = [
  { section: "Hero (haut de l'accueil)", fields: [
    { k: "hero_eyebrow", label: "Sur-titre", placeholder: "Restaurant portugais · Trappes", multiline: false },
    { k: "hero_title_line1", label: "Titre — 1ère ligne", placeholder: "Saveurs du Portugal,", multiline: false },
    { k: "hero_title_line2", label: "Titre — 2ème ligne", placeholder: "chaleur de la famille.", multiline: false },
    { k: "hero_description", label: "Description sous le titre", placeholder: "Plats du jour cuisinés avec passion en semaine…", multiline: true },
  ]},
  { section: "Carte 'Aujourd'hui au Globo' (droite du hero)", fields: [
    { k: "hero_today_dish", label: "Plat du jour", placeholder: "Cozido à Portuguesa — 19,00 €", multiline: false },
    { k: "hero_today_soiree", label: "Soirée du week-end", placeholder: "Vendredi · Fado live, 20h", multiline: false },
    { k: "hero_today_hours", label: "Ouverture", placeholder: "12h–14h30 · 19h–22h30", multiline: false },
  ]},
  { section: "Section 'À propos'", fields: [
    { k: "about_eyebrow", label: "Sur-titre", placeholder: "A casa do Globo", multiline: false },
    { k: "about_title", label: "Titre principal", placeholder: "Une table portugaise vivante au cœur de Trappes", multiline: false },
    { k: "about_text", label: "Texte", placeholder: "Chez nous, chaque plat raconte une histoire…", multiline: true },
  ]},
  { section: "Section CTA (bas de l'accueil)", fields: [
    { k: "cta_title", label: "Titre", placeholder: "Une envie de Portugal ?", multiline: false },
    { k: "cta_text", label: "Texte", placeholder: "Réservez votre table en quelques clics…", multiline: true },
  ]},
];

function ContentPanel({ code }) {
  const [content, setContent] = useState({});
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [dirty, setDirty] = useState(false);

  useEffect(() => {
    api.get("/site/content").then(r => setContent(r.data || {})).finally(() => setLoading(false));
  }, []);

  const update = (k, v) => { setContent({ ...content, [k]: v }); setDirty(true); };

  const save = async () => {
    setSaving(true);
    try {
      await adminAxios(code).put("/admin/site/content", { content });
      toast.success("Contenu sauvegardé !");
      setDirty(false);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Erreur");
    } finally { setSaving(false); }
  };

  if (loading) return <p className="text-[#5C5A56]">Chargement…</p>;

  return (
    <div data-testid="content-panel" className="space-y-6">
      <div className="card-warm p-5 bg-gradient-to-br from-[#F4F1EA] to-white flex justify-between items-center gap-4 flex-wrap sticky top-24 z-10">
        <div>
          <h3 className="text-xl">Textes du site</h3>
          <p className="text-sm text-[#5C5A56]">Modifiez librement, tout apparaît sur votre page d'accueil en temps réel.</p>
        </div>
        <button onClick={save} disabled={saving || !dirty} className="btn-primary" data-testid="content-save-btn">
          {saving ? <><Loader2 className="animate-spin mr-2" size={18}/> Sauvegarde…</> : <><Save size={16} className="mr-2"/> Sauvegarder{dirty && " *"}</>}
        </button>
      </div>

      {CONTENT_FIELDS.map((section, si) => (
        <div key={si} className="card-warm p-6" data-testid={`content-section-${si}`}>
          <h4 className="font-serif-display text-xl mb-4 text-[#1F476B]">{section.section}</h4>
          <div className="space-y-4">
            {section.fields.map((f) => (
              <div key={f.k}>
                <label className="text-sm font-medium mb-1.5 block">{f.label}</label>
                {f.multiline ? (
                  <textarea rows={4} className="input-warm" value={content[f.k] || ""} onChange={(e) => update(f.k, e.target.value)} placeholder={f.placeholder} data-testid={`content-field-${f.k}`}/>
                ) : (
                  <input className="input-warm" value={content[f.k] || ""} onChange={(e) => update(f.k, e.target.value)} placeholder={f.placeholder} data-testid={`content-field-${f.k}`}/>
                )}
              </div>
            ))}
          </div>
        </div>
      ))}
    </div>
  );
}

// ============ EVENTS PANEL ============
function EventsPanel({ code }) {
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null); // event object or null (closed) or {} (new)

  const refresh = () => {
    setLoading(true);
    api.get("/events").then(r => setEvents(r.data || [])).finally(() => setLoading(false));
  };
  useEffect(() => { refresh(); }, []);

  const remove = async (id) => {
    if (!window.confirm("Supprimer cette soirée ?")) return;
    try { await adminAxios(code).delete(`/admin/events/${id}`); toast.success("Supprimée"); refresh(); }
    catch { toast.error("Erreur"); }
  };

  const startNew = () => setEditing({ title: "", description: "", date: "", time: "", image_url: "", poster_url: "", special_menu: [] });

  return (
    <div data-testid="events-panel" className="space-y-6">
      <div className="flex justify-between items-center flex-wrap gap-3">
        <div>
          <h3 className="text-2xl">Soirées & événements ({events.length})</h3>
          <p className="text-sm text-[#5C5A56]">Créez une soirée avec date, affiche PDF et menu spécial.</p>
        </div>
        <button onClick={startNew} className="btn-primary" data-testid="event-new-btn"><Plus size={16} className="mr-2"/> Nouvelle soirée</button>
      </div>

      {editing && <EventEditor code={code} initial={editing} onClose={() => { setEditing(null); refresh(); }} />}

      {loading ? <p className="text-[#5C5A56]">Chargement…</p> : events.length === 0 ? (
        <p className="text-[#5C5A56]" data-testid="events-panel-empty">Aucune soirée. Créez la première !</p>
      ) : (
        <div className="grid md:grid-cols-2 gap-4">
          {events.map((e) => (
            <div key={e.id} className="card-warm overflow-hidden" data-testid={`admin-event-${e.id}`}>
              {e.image_url && <div className="aspect-[16/9] overflow-hidden"><img src={e.image_url} alt={e.title} className="w-full h-full object-cover"/></div>}
              <div className="p-5">
                <div className="text-xs uppercase tracking-widest text-[#C04E38] mb-2">{new Date(e.date).toLocaleDateString("fr-FR", { day: "numeric", month: "long", year: "numeric" })} {e.time && `· ${e.time}`}</div>
                <h4 className="text-xl mb-2">{e.title}</h4>
                <p className="text-sm text-[#5C5A56] line-clamp-2">{e.description}</p>
                <div className="flex gap-3 text-xs mt-3">
                  {e.poster_url && <span className="inline-flex items-center gap-1 text-[#1F476B]"><FileText size={12}/> Affiche</span>}
                  {e.special_menu?.length > 0 && <span className="inline-flex items-center gap-1 text-[#1F476B]"><ScanText size={12}/> Menu ({e.special_menu.length} plats)</span>}
                </div>
                <div className="flex gap-2 mt-4">
                  <button onClick={() => setEditing(e)} className="px-3 py-1.5 text-xs rounded-lg bg-blue-50 text-blue-700 hover:bg-blue-100" data-testid={`event-edit-${e.id}`}><Edit3 size={12} className="inline mr-1"/> Éditer</button>
                  <button onClick={() => remove(e.id)} className="px-3 py-1.5 text-xs rounded-lg bg-red-50 text-red-700 hover:bg-red-100" data-testid={`event-delete-${e.id}`}><Trash2 size={12} className="inline mr-1"/> Supprimer</button>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

function EventEditor({ code, initial, onClose }) {
  const [form, setForm] = useState({
    title: initial.title || "",
    description: initial.description || "",
    date: initial.date || "",
    time: initial.time || "",
    image_url: initial.image_url || "",
    poster_url: initial.poster_url || "",
    special_menu: initial.special_menu || [],
  });
  const [uploadingImage, setUploadingImage] = useState(false);
  const [uploadingPoster, setUploadingPoster] = useState(false);
  const [saving, setSaving] = useState(false);
  const imageRef = useRef(null);
  const posterRef = useRef(null);

  const update = (k, v) => setForm({ ...form, [k]: v });

  const uploadImage = async (e) => {
    const f = e.target.files?.[0]; if (!f) return;
    if (!["image/jpeg","image/png","image/webp","image/jpg"].includes(f.type)) { toast.error("JPEG/PNG/WEBP requis"); return; }
    const fd = new FormData();
    fd.append("file", f);
    fd.append("alt", form.title || "Événement");
    setUploadingImage(true);
    try {
      const res = await adminAxios(code).post("/admin/gallery/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
      update("image_url", res.data.url);
      toast.success("Photo uploadée");
    } catch (err) { toast.error(err?.response?.data?.detail || "Erreur"); }
    finally { setUploadingImage(false); e.target.value = ""; }
  };

  const uploadPoster = async (e) => {
    const f = e.target.files?.[0]; if (!f) return;
    if (!["application/pdf", "image/jpeg", "image/png", "image/webp"].includes(f.type)) { toast.error("PDF, JPEG, PNG ou WEBP requis"); return; }
    const fd = new FormData();
    fd.append("file", f);
    setUploadingPoster(true);
    try {
      const res = await adminAxios(code).post("/admin/events/upload-poster", fd, { headers: { "Content-Type": "multipart/form-data" } });
      update("poster_url", res.data.url);
      toast.success(res.data.is_pdf ? "PDF uploadé" : "Affiche uploadée");
    } catch (err) { toast.error(err?.response?.data?.detail || "Erreur"); }
    finally { setUploadingPoster(false); e.target.value = ""; }
  };

  const addMenuItem = () => update("special_menu", [...form.special_menu, { name: "", description: "", price: "" }]);
  const updateMenuItem = (i, k, v) => {
    const c = [...form.special_menu]; c[i] = { ...c[i], [k]: v }; update("special_menu", c);
  };
  const removeMenuItem = (i) => update("special_menu", form.special_menu.filter((_, idx) => idx !== i));

  const save = async () => {
    if (!form.title.trim() || !form.description.trim() || !form.date) {
      toast.error("Titre, description et date obligatoires");
      return;
    }
    setSaving(true);
    try {
      const cleanMenu = form.special_menu.filter(it => it.name?.trim());
      const payload = { ...form, special_menu: cleanMenu };
      if (initial.id) {
        await adminAxios(code).patch(`/admin/events/${initial.id}`, payload);
        toast.success("Soirée mise à jour");
      } else {
        await adminAxios(code).post("/admin/events", payload);
        toast.success("Soirée créée");
      }
      onClose();
    } catch (err) { toast.error(err?.response?.data?.detail || "Erreur"); }
    finally { setSaving(false); }
  };

  const posterIsPdf = form.poster_url?.toLowerCase().endsWith(".pdf");

  return (
    <div className="card-warm p-6 md:p-8 border-[#C04E38] border-2" data-testid="event-editor">
      <div className="flex justify-between items-center mb-6">
        <h4 className="text-2xl">{initial.id ? "Modifier la soirée" : "Nouvelle soirée"}</h4>
        <button onClick={onClose} className="p-2 rounded-lg hover:bg-[#F4F1EA]" data-testid="event-editor-close"><X size={18}/></button>
      </div>

      <div className="grid md:grid-cols-2 gap-5 mb-5">
        <div>
          <label className="text-sm font-medium mb-1.5 block">Titre *</label>
          <input className="input-warm" value={form.title} onChange={(e) => update("title", e.target.value)} placeholder="Soirée Fado & Saudade" data-testid="event-title"/>
        </div>
        <div className="grid grid-cols-2 gap-3">
          <div>
            <label className="text-sm font-medium mb-1.5 block">Date *</label>
            <input type="date" className="input-warm" value={form.date} onChange={(e) => update("date", e.target.value)} data-testid="event-date"/>
          </div>
          <div>
            <label className="text-sm font-medium mb-1.5 block">Heure</label>
            <input type="time" className="input-warm" value={form.time} onChange={(e) => update("time", e.target.value)} data-testid="event-time"/>
          </div>
        </div>
      </div>

      <div className="mb-5">
        <label className="text-sm font-medium mb-1.5 block">Description *</label>
        <textarea rows={3} className="input-warm" value={form.description} onChange={(e) => update("description", e.target.value)} placeholder="Live de chants traditionnels du Portugal…" data-testid="event-description"/>
      </div>

      <div className="grid md:grid-cols-2 gap-5 mb-6">
        <div>
          <label className="text-sm font-medium mb-1.5 block">Photo d'ambiance (JPEG/PNG)</label>
          <input ref={imageRef} type="file" accept="image/png,image/jpeg,image/webp" className="hidden" onChange={uploadImage} data-testid="event-image-input"/>
          <button type="button" onClick={() => imageRef.current?.click()} disabled={uploadingImage} className="btn-secondary !py-2 text-sm" data-testid="event-image-btn">
            {uploadingImage ? <><Loader2 className="animate-spin mr-2" size={14}/> Upload…</> : <><ImageIcon size={14} className="mr-2"/> {form.image_url ? "Remplacer la photo" : "Choisir une photo"}</>}
          </button>
          {form.image_url && <img src={form.image_url} alt="" className="mt-3 h-32 rounded-lg object-cover border border-[#E6E1D6]"/>}
        </div>
        <div>
          <label className="text-sm font-medium mb-1.5 block">Affiche PDF (ou image)</label>
          <input ref={posterRef} type="file" accept="application/pdf,image/png,image/jpeg,image/webp" className="hidden" onChange={uploadPoster} data-testid="event-poster-input"/>
          <button type="button" onClick={() => posterRef.current?.click()} disabled={uploadingPoster} className="btn-secondary !py-2 text-sm" data-testid="event-poster-btn">
            {uploadingPoster ? <><Loader2 className="animate-spin mr-2" size={14}/> Upload…</> : <><FileImage size={14} className="mr-2"/> {form.poster_url ? "Remplacer l'affiche" : "Choisir une affiche"}</>}
          </button>
          {form.poster_url && (
            <a href={form.poster_url} target="_blank" rel="noreferrer" className="mt-3 inline-flex items-center gap-2 text-xs text-[#1F476B] hover:text-[#C04E38]" data-testid="event-poster-preview">
              {posterIsPdf ? <FileText size={14}/> : <ImageIcon size={14}/>}
              {posterIsPdf ? "Voir le PDF" : "Voir l'affiche"}
            </a>
          )}
        </div>
      </div>

      {/* Special menu */}
      <div className="mb-6">
        <div className="flex justify-between items-center mb-3">
          <label className="text-sm font-medium">Menu spécial de la soirée</label>
          <button type="button" onClick={addMenuItem} className="btn-ghost text-sm !py-1.5" data-testid="event-menu-add"><Plus size={14} className="mr-1"/> Ajouter un plat</button>
        </div>
        {form.special_menu.length === 0 ? (
          <p className="text-xs text-[#5C5A56] italic">Aucun plat. Cliquez sur "Ajouter un plat" pour créer un menu dédié.</p>
        ) : (
          <div className="space-y-2">
            {form.special_menu.map((it, i) => (
              <div key={i} className="grid grid-cols-12 gap-2 items-start bg-[#F4F1EA] rounded-xl p-3" data-testid={`event-menu-item-${i}`}>
                <input className="input-warm col-span-3 !py-2 text-sm" value={it.name} onChange={(e) => updateMenuItem(i, "name", e.target.value)} placeholder="Nom du plat"/>
                <input className="input-warm col-span-6 !py-2 text-sm" value={it.description} onChange={(e) => updateMenuItem(i, "description", e.target.value)} placeholder="Description"/>
                <input className="input-warm col-span-2 !py-2 text-sm" value={it.price} onChange={(e) => updateMenuItem(i, "price", e.target.value)} placeholder="Prix"/>
                <button type="button" onClick={() => removeMenuItem(i)} className="p-2 rounded-lg bg-red-50 text-red-700 hover:bg-red-100 col-span-1 justify-self-end" data-testid={`event-menu-remove-${i}`}><Trash2 size={14}/></button>
              </div>
            ))}
          </div>
        )}
      </div>

      <div className="flex gap-3 justify-end border-t border-[#E6E1D6] pt-4">
        <button onClick={onClose} className="btn-ghost" data-testid="event-cancel-btn">Annuler</button>
        <button onClick={save} disabled={saving} className="btn-primary" data-testid="event-save-btn">
          {saving ? <><Loader2 className="animate-spin mr-2" size={16}/> Sauvegarde…</> : <><Save size={16} className="mr-2"/> {initial.id ? "Mettre à jour" : "Créer la soirée"}</>}
        </button>
      </div>
    </div>
  );
}

